import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { fmtNumber, fmtPct } from "../lib/utils";
import { AreaChart, Area, ResponsiveContainer } from "recharts";
import { TrendingDown, TrendingUp, AlertTriangle, Filter, Database, X } from "lucide-react";
import ActivityRecordsTable from "../components/ActivityRecordsTable";

const ACTIVITY_KPI_IDS = new Set([
  "scope1_emissions", "scope2_emissions", "scope3_emissions",
  "energy_total", "energy_renewable_pct", "ewaste_recycled",
]);

export default function Scorecard() {
  const [data, setData] = useState(null);
  const [params, setParams] = useSearchParams();
  const issueFilter = params.get("issue") || "";
  const [search, setSearch] = useState("");
  const [drawerKpi, setDrawerKpi] = useState(null);

  useEffect(() => { api.get("/scorecard").then(r => setData(r.data)); }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.rows.filter(r => {
      if (issueFilter && r.kpi.material_issue !== issueFilter) return false;
      if (search && !r.kpi.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [data, issueFilter, search]);

  if (!data) return <div className="p-6 text-ink-soft">Loading…</div>;

  return (
    <div className="p-6 space-y-4">
      <div>
        <div className="section-label">Scorecard</div>
        <h1 className="text-2xl font-bold tracking-tight mt-1">KPI Scorecard</h1>
        <p className="text-sm text-ink-soft">Single auditable view across all material issues, with confidence, trend and standards mapping.</p>
      </div>

      {/* Filters */}
      <div className="card p-3 flex flex-wrap items-center gap-2" data-testid="scorecard-filters">
        <div className="flex items-center gap-2 pr-3 border-r border-border">
          <Filter size={14} className="text-ink-soft" />
          <span className="dlabel">Material Issue</span>
        </div>
        <button
          data-testid="filter-all"
          onClick={() => setParams({})}
          className={"pill " + (!issueFilter ? "pill-success" : "")}>All</button>
        {data.material_issues.map((m) => (
          <button
            key={m}
            data-testid={`filter-${m.replace(/\s+/g,'-').toLowerCase()}`}
            onClick={() => setParams({ issue: m })}
            className={"pill " + (issueFilter === m ? "pill-success" : "")}
          >{m}</button>
        ))}
        <div className="ml-auto">
          <input
            data-testid="scorecard-search"
            value={search} onChange={e=>setSearch(e.target.value)}
            placeholder="Search KPI…"
            className="border border-border rounded-md px-3 py-1.5 text-sm outline-none focus:border-brand"
          />
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden" data-testid="scorecard-table">
        <table className="dense">
          <thead>
            <tr>
              <th>KPI</th>
              <th>Material Issue</th>
              <th className="text-right">Current</th>
              <th className="text-right">Baseline</th>
              <th className="text-right">Target</th>
              <th className="text-right">QoQ</th>
              <th className="text-right">YoY</th>
              <th>Trend</th>
              <th>Confidence</th>
              <th>Anomalies</th>
              <th>Standards</th>
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.kpi.id} data-testid={`row-${r.kpi.id}`}>
                <td>
                  <Link to={`/kpi/${r.kpi.id}`} className="font-medium hover:text-brand">{r.kpi.name}</Link>
                  <div className="text-[11px] text-ink-soft">{r.kpi.unit}</div>
                </td>
                <td className="text-ink-soft">{r.kpi.material_issue}</td>
                <td className="num font-semibold">{fmtNumber(r.current_value)}</td>
                <td className="num text-ink-soft">{fmtNumber(r.baseline_value)}</td>
                <td className="num">{fmtNumber(r.target_value)}</td>
                <td className={"num " + (r.improving ? "text-status-success" : "text-status-error")}>
                  {fmtPct(r.qoq_change_pct)}
                </td>
                <td className={"num " + (r.improving ? "text-status-success" : "text-status-error")}>
                  {fmtPct(r.yoy_change_pct)}
                </td>
                <td>
                  <div className="w-28 h-8">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={r.spark.map(v=>({v}))} margin={{top:2,right:0,left:0,bottom:0}}>
                        <Area dataKey="v" stroke={r.improving ? "#10B981" : "#EF4444"} fill="transparent" strokeWidth={1.5} dot={false}/>
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </td>
                <td>
                  <div className="flex items-center gap-1">
                    <div className="w-20 h-1.5 bg-surface rounded-full overflow-hidden">
                      <div className="h-full bg-brand" style={{width: `${r.confidence*100}%`}}/>
                    </div>
                    <span className="text-[11px] text-ink-soft font-mono">{Math.round(r.confidence*100)}%</span>
                  </div>
                </td>
                <td>
                  {r.open_anomalies > 0 ? (
                    <span className="pill pill-error"><AlertTriangle size={11}/> {r.open_anomalies}</span>
                  ) : (
                    <span className="pill pill-success">clean</span>
                  )}
                </td>
                <td>
                  <div className="flex flex-wrap gap-1">
                    {r.kpi.standards.slice(0,3).map(s => (
                      <span key={s} className="pill text-[10px]">{s}</span>
                    ))}
                  </div>
                </td>
                <td>
                  {ACTIVITY_KPI_IDS.has(r.kpi.id) ? (
                    <button
                      data-testid={`source-${r.kpi.id}`}
                      onClick={() => setDrawerKpi(r.kpi)}
                      className="btn btn-ghost text-xs"
                      title="View source business activity data"
                    >
                      <Database size={11}/> View
                    </button>
                  ) : (
                    <span className="text-[10px] text-ink-soft">—</span>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={12} className="text-center text-ink-soft py-6">No KPIs match the filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Source-data drawer */}
      {drawerKpi && (
        <div className="fixed inset-0 z-40 flex" data-testid="source-drawer">
          <div className="flex-1 bg-black/40" onClick={() => setDrawerKpi(null)} />
          <div className="w-full max-w-5xl bg-white h-full overflow-y-auto shadow-2xl">
            <div className="px-5 py-3 border-b border-border flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <div className="section-label">Source data</div>
                <div className="font-semibold text-sm mt-0.5">{drawerKpi.name} <span className="text-ink-soft font-normal">· {drawerKpi.material_issue}</span></div>
              </div>
              <div className="flex items-center gap-2">
                <Link to={`/kpi/${drawerKpi.id}`} className="btn btn-secondary text-xs">Open KPI page</Link>
                <button data-testid="close-source-drawer" onClick={() => setDrawerKpi(null)} className="btn btn-ghost"><X size={14}/></button>
              </div>
            </div>
            <div className="p-5">
              <ActivityRecordsTable kpiId={drawerKpi.id} compact />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
